"use client"

import React, { useState, useEffect } from "react"
import { MessageSquare } from "lucide-react"
import { Tabs, TabsList, TabsContent, TabsTrigger } from "../components/ui/tabs.tsx"
import { SearchBar } from "../components/support/SearchBar"
import { QuickHelpCards } from "../components/support/QuickHelpCards"
import { FAQSection } from "../components/support/FAQSection"
import { TicketsTable } from "../components/support/TicketsTable"
import SidebarChat from "../components/support/SidebarChat"
import { chatService } from "../services/chatService"

export default function SupportPage() {
  const [activeTab, setActiveTab] = useState("help")
  const [searchQuery, setSearchQuery] = useState("")
  const [isChatOpen, setIsChatOpen] = useState(false)

  // Sample data for components
  const faqs = [
    {
      question: "How do I reset my campus account password?",
      answer: "To reset your password, follow these steps:\n1. Go to the login page\n2. Click on 'Forgot Password'\n3. Enter your student email address\n4. Check your email and click the reset link\n5. Create a new password following the security guidelines",
    },
    {
      question: "Where can I find my class schedule?",
      answer: "Your class schedule is easily accessible:\n1. Click 'Academics' in the main sidebar\n2. Select 'Schedule' from the menu\n3. View your weekly schedule with class times and rooms\n4. Use filters to view specific days or subjects",
    },
    {
      question: "How do I submit an assignment?",
      answer: "To submit your assignment:\n1. Go to your class page\n2. Select the 'Assignments' tab\n3. Find your assignment and click 'Submit'\n4. Upload your files or type your submission\n5. Click 'Submit' and wait for confirmation",
    },
    {
      question: "How do I track my attendance?",
      answer: "Track your attendance easily:\n1. Navigate to the 'Attendance' section\n2. View your attendance percentage by subject\n3. See detailed attendance records by date\n4. Download attendance reports if needed",
    },
    {
      question: "How do I contact my teachers?",
      answer: "Contact your teachers through:\n1. The messaging system in your class page\n2. Schedule virtual office hours\n3. Send direct messages through the platform\n4. Use the class discussion forums",
    }
  ]

  const tickets = [
    {
      id: "TCK-1001",
      title: "Unable to access online library resources",
      status: "open",
      category: "Academic",
      date: "2023-05-15",
      priority: "High",
    },
    {
      id: "TCK-1002",
      title: "Issue with course registration system",
      status: "in-progress",
      category: "Technical",
      date: "2023-05-14",
      priority: "Medium",
    },
  ]

  return (
    <div className="min-h-screen relative">
      <div className={`transition-all duration-300 ease-in-out ${isChatOpen ? 'mr-[400px]' : ''}`}>
        <div className="p-6 bg-gray-50 min-h-screen">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Center</h1>
              <p className="text-gray-600">Get help with your campus experience and find answers to your questions</p>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
            </div>

            {/* Quick Help Cards */}
            <QuickHelpCards 
              isChatOpen={isChatOpen}
              onChatClick={() => setIsChatOpen(!isChatOpen)} 
            />

            {/* Main Content Tabs */}
            <Tabs defaultValue="help" onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 w-full max-w-md mb-6">
                <TabsTrigger value="help">Help Center</TabsTrigger>
                <TabsTrigger value="tickets">My Tickets</TabsTrigger>
              </TabsList>

              {/* Help Center Tab */}
              <TabsContent value="help">
                <FAQSection faqs={faqs} />
              </TabsContent>

              {/* My Tickets Tab */}
              <TabsContent value="tickets">
                <TicketsTable tickets={tickets} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Support Chat */}
      <SidebarChat
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </div>
  )
}
